/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useEffect, useState } from "react";
import AppText from "@/components/other/AppText";
import { cn } from "@/lib/utils";
import { CheckMark } from "@/assets/Icons";
import Image from "next/image";
import Carousel from "@/components/other/Carousel";
import { SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { useMediaQuery } from "@/hooks/mediaQuery";
import ContentLayout from "../layouts/ContentLayout";
import { Separator } from "../ui/separator";
import { AppButton } from "../buttons/AppButton";
import { MessageSquareText } from "lucide-react";
import UsedProductBanner from "./UsedProductBanner";
import CustomLink from "../other/CustomLink";
import { IDict } from "../other/constants";
import { BUTTON_SMALL } from "@/styles";
import { useBreadcrumbsLabel } from "@/store/clientStore";

const SalesProduct = ({ hero, dict }: { hero: any; dict: IDict }) => {
  const [selectedProduct, setSelectedProduct] = useState<{ image: string; imgAlt: string }>(hero.images[0]);
  const slidesPerView = hero.images.length;
  const isSmallScreen = useMediaQuery("(max-width: 1024px)");
  const { setUnitNumber } = useBreadcrumbsLabel();

  const numberFormatter = new Intl.NumberFormat();
  // const telPhone = "866-123-4567"; // TODO: Pull this value from contentful once added...

  const UsedProductBannerProps = {
    bg: "bg-navy-blue",
    heading: hero.notificationBannerHeading,
    subheading: hero.notificationBannerSubheading,
    ctaLabel: hero.notificationBannerCtaLabel,
    ctaLink: hero.notificationBannerCtaLink,
    ctaTheme: "secondary" as const,
    heroIcon: "home" as const,
    textColor: "text-white",
  };

  useEffect(() => {
    if (hero && hero.unitNumber) {
      setUnitNumber(hero.unitNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hero]);

  const renderImage = (imageUrl: string, index: number, alt: string): JSX.Element => {
    return (
      <SwiperSlide key={imageUrl + index} className="w-[96px] min-h-[96px] max-h-[200px] lg:max-h-full">
        <Image
          onClick={() => setSelectedProduct({ image: imageUrl, imgAlt: alt })}
          src={imageUrl || "/placeholders/no-preview.png"}
          alt={alt}
          sizes="100vw"
          width={0}
          height={0}
          className="object-cover w-full h-full"
        />
      </SwiperSlide>
    );
  };

  const onClickHandler = (e: any) => {
    e.preventDefault();
    console.log(`Opening: Chat`);
    const button = document.querySelector("#embeddedMessagingConversationButton") as HTMLButtonElement;
    button?.click();
  };

  return (
    <>
      <ContentLayout>
        {/** tab content */}
        <div className="max-lg:flex-col-reverse lg:mt-12 flex flex-1 mt-4">
          <div className="flex flex-col flex-[0.5] mr-[40px]">
            <div className="lg:mt-0 flex flex-wrap gap-4 mt-10 mb-6">
              <AppText type={"LABEL_LARGE"} className="text-royal-blue">
                {hero.branchName || "N/A"}
              </AppText>
              <Separator orientation="vertical" className="bg-[#9BA4AB]" />
              {hero.productType && (
                <AppText type={"LABEL_LARGE"} className="text-royal-blue">
                  {hero.productType}
                </AppText>
              )}
            </div>

            <AppText type={"HEADLINE_MEDIUM"} as="h1" className="mb-6">
              {hero.name}
            </AppText>

            <div className="mb-14 flex flex-wrap items-center gap-6">
              {hero.discountedPrice ? (
                <div className=" flex flex-wrap items-center gap-4">
                  <span className="text-18 text-center line-through">${numberFormatter.format(hero.price)}</span>
                  <AppText
                    type="BODY_LARGE_EMPHASIS"
                    className="text-[#DE350B] !font-saira text-18 font-500 tracking-[-0.1px] text-center"
                  >
                    ${numberFormatter.format(hero.discountedPrice)}
                  </AppText>
                </div>
              ) : (
                <AppText type="BODY_LARGE_EMPHASIS" className="mt-auto text-center">
                  ${numberFormatter.format(hero.price)}
                </AppText>
              )}

              {hero.obo && (
                <div className="bg-mellow rounded-2xl px-3 py-1 text-14 font-[500] font-sairaSemiCondensed">OBO</div>
              )}
            </div>

            <div className="mb-14 flex flex-wrap items-center gap-8">
              <AppButton label={dict.purchaseInquiry} intent="primary" link={`/request-quote-buy/${hero.unitNumber}`} />

              <div className="flex gap-8">
                {hero.phoneNumber && (
                  <div className="border-b-[1px] border-black text-center flex items-center">
                    <CustomLink className={cn(BUTTON_SMALL)} href={`tel:${hero.phoneNumber}`}>
                      {`${dict.callUsOn} ${hero.phoneNumber}`}
                    </CustomLink>
                  </div>
                )}

                <div
                  title={dict.openChat}
                  onClick={onClickHandler}
                  className="h-9 w-9 bg-black-10 flex items-center justify-center rounded-full cursor-pointer"
                >
                  <MessageSquareText size={20} />
                </div>
              </div>
            </div>

            <div className="flex items-start mb-6">
              <AppText type="BODY_SMALL" className="mt-auto text-center">
                Unit #: {hero.unitNumber}
              </AppText>
            </div>

            {!!hero?.productFeatures && !!hero.productFeatures.length && (
              <div className={"pl-[16px] flex flex-col border-l-[1px] border-l-slate-40"}>
                <AppText type={"BODY_MEDIUM_EMPHASIS"}>{dict.productFeatures}</AppText>

                <div className={"mt-2 flex flex-wrap gap-y-4 gap-x-8"}>
                  {(hero.productFeatures || []).map((item: string, index: number) => (
                    <div key={index} className={"flex items-center"}>
                      <CheckMark />

                      <AppText className={"ml-[8px]"} type={"BODY_SMALL_EMPHASIS"}>
                        {item}
                      </AppText>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className={"relative flex flex-[0.5] flex-col  items-center overflow-hidden"}>
            {!isSmallScreen && (
              <div className={"w-[80%]"}>
                <Image
                  src={selectedProduct.image}
                  alt={selectedProduct.imgAlt}
                  sizes="(max-width: 1024px) 100vw, 650px"
                  width={0}
                  height={0}
                  className="object-cover w-full max-h-[323px]"
                />
              </div>
            )}
            {!!hero.images.length && (
              <div className={"relative w-full flex justify-center max-lg:justify-end flex-col mt-[24px]"}>
                <Carousel
                  key={slidesPerView}
                  wrapperClassName={cn("flex flex-1 relative px-0 lg:px-12")}
                  loop={true}
                  slidesPerView={slidesPerView}
                  slidesPerGroup={1}
                  spaceBetween={16}
                  modules={[Pagination]}
                  rewind={true}
                  pagination={isSmallScreen && { el: ".swiper-custom-pagination" }}
                  paginationType={"sideArrows"}
                  breakpoints={{
                    320: { slidesPerView: 1 },
                    640: { slidesPerView: 1 },
                    1024: { slidesPerView: Math.max(slidesPerView - 1, 1) },
                    1280: { slidesPerView: slidesPerView },
                    1536: { slidesPerView: slidesPerView },
                  }}
                >
                  {hero.images.map((img: { image: string; imgAlt: string }, index: number) =>
                    renderImage(img.image, index, img.imgAlt)
                  )}
                </Carousel>
                {isSmallScreen && (
                  <div className="w-full swiper-custom-pagination justify-center items-end flex pb-[12px] mt-[24px]" />
                )}
              </div>
            )}
          </div>
        </div>
      </ContentLayout>

      <ContentLayout innerClass=" sm:pb-[80px] pb-[48px] pt-0 sm:pt-0">
        <UsedProductBanner {...UsedProductBannerProps} />
      </ContentLayout>
    </>
  );
};

export default React.memo(SalesProduct);
